import type {
  UseSpringProps as UseSpringPropsBase,
  UseTransitionProps as UseTransitionPropsBase
} from 'react-spring'
import {
  useChain,
  useSpring as useSpringBase,
  useSpringRef,
  useTrail,
  useTransition as useTransitionBase
} from 'react-spring'

interface UseSpringProps extends Omit<UseSpringPropsBase, 'ref'> {}

const useSpring = <P extends UseSpringProps>(props: P) => {
  const ref = useSpringRef()
  const spring = useSpringBase({
    ...props,
    ref
  })
  return [spring, ref] as const
}

interface UseTransitionProps<T>
  extends Omit<UseTransitionPropsBase<T>, 'ref'> {}

const useTransition = <T, P extends UseTransitionProps<T>>(
  data: T,
  props: P
) => {
  const ref = useSpringRef()
  const transition = useTransitionBase(data, {
    ...props,
    config: { duration: 150, ...props.config },
    ref
  })
  return [transition, ref] as const
}

export const useWebRecorderAnimation = (initiated: boolean, status: string) => {
  const isRecording = ['recording', 'paused'].includes(status)

  const [recordButtonStyles, recordButtonSpringRef] = useSpring({
    config: { duration: 150 },
    from: { opacity: 1, y: 0 },
    to: {
      opacity: initiated ? 0 : 1,
      y: initiated ? 20 : 0
    }
  })

  const [recordingControlsStyles, recordingControlsSpringRef] = useSpring({
    config: {
      mass: 5,
      tension: 2000,
      friction: 200
    },
    delay: isRecording ? 150 : 0,
    from: { opacity: 0, y: 80 },
    to: {
      opacity: isRecording ? 1 : 0,
      y: isRecording ? 0 : 80
    }
  })

  const [wrapperTransition, wrapperTransitionRef] = useTransition(
    !isRecording,
    {
      reverse: !isRecording,
      from: { opacity: 0 },
      enter: { opacity: 1 },
      leave: { opacity: 0 }
    }
  )

  const mainContentTrailRef = useSpringRef()
  const mainContentTransition = useTrail(2, {
    ref: mainContentTrailRef,
    config: { mass: 5, tension: 2000, friction: 200 },
    from: { opacity: 0, scale: 0 },
    opacity: isRecording ? 1 : 0,
    scale: isRecording ? 1 : 0,
    delay: isRecording ? 300 : 0
  })

  const [chainRefs, chainTimeSteps] = initiated
    ? [
        [
          recordButtonSpringRef,
          wrapperTransitionRef,
          recordingControlsSpringRef,
          mainContentTrailRef
        ] as const,
        [0, 0.3, 0.15, 0]
      ]
    : [
        [
          mainContentTrailRef,
          recordingControlsSpringRef,
          wrapperTransitionRef,
          recordButtonSpringRef
        ] as const,
        [0, 0, 0.3, 0.3]
      ]

  useChain(chainRefs, chainTimeSteps)

  return {
    recordButtonStyles,
    recordingControlsStyles,
    mainContentTransition,
    wrapperTransition
  }
}
